import { useEffect, useState } from "react";
import { api, formatApiErrorDetail, fileSrc } from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Building2, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function StudioBrandingDialog({ open, onOpenChange }) {
  const { user, setUser } = useAuth();
  const [businessName, setBusinessName] = useState(user?.business_name || "");
  const [address, setAddress] = useState(user?.company_address || "");
  const [phone, setPhone] = useState(user?.company_phone || "");
  const [companyEmail, setCompanyEmail] = useState(user?.company_email || "");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(user?.logo || null);
  const [busy, setBusy] = useState(false);

  // Re-sync local state from the (possibly updated) user whenever the dialog opens.
  useEffect(() => {
    if (open) {
      setBusinessName(user?.business_name || "");
      setAddress(user?.company_address || "");
      setPhone(user?.company_phone || "");
      setCompanyEmail(user?.company_email || "");
      setPreview(user?.logo || null);
      setFile(null);
    }
  }, [open, user]);

  const pick = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const removeLogo = () => { setFile(null); setPreview(null); };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("business_name", businessName);
      fd.append("company_address", address);
      fd.append("company_phone", phone);
      fd.append("company_email", companyEmail);
      if (file) fd.append("file", file);
      else if (!preview) fd.append("remove_logo", "true");
      const { data } = await api.post("/me/branding", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUser(data);
      toast.success("Studio branding updated");
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save branding");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none border-[#EBE5E0] bg-white" data-testid="branding-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-[#2B2622]">Studio branding</DialogTitle>
          <DialogDescription className="text-sm text-[#5C5651]">
            Add your business name and logo. They appear on every project header you manage.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-5 mt-2">
          <div>
            <Label className="text-xs uppercase tracking-wider text-[#5C5651]">Business name</Label>
            <Input data-testid="branding-name-input" value={businessName}
              onChange={(e) => setBusinessName(e.target.value)} placeholder="e.g. Marlowe Interiors"
              className="mt-1 rounded-none border-[#EBE5E0] focus-visible:ring-0 focus-visible:border-[#A68A6B]" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-[#5C5651]">Company address</Label>
            <Input data-testid="branding-address-input" value={address}
              onChange={(e) => setAddress(e.target.value)} placeholder="Street, City, State ZIP"
              className="mt-1 rounded-none border-[#EBE5E0] focus-visible:ring-0 focus-visible:border-[#A68A6B]" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#5C5651]">Phone</Label>
              <Input data-testid="branding-phone-input" value={phone}
                onChange={(e) => setPhone(e.target.value)} placeholder="(555) 555-5555"
                className="mt-1 rounded-none border-[#EBE5E0] focus-visible:ring-0 focus-visible:border-[#A68A6B]" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#5C5651]">Email</Label>
              <Input data-testid="branding-email-input" type="email" value={companyEmail}
                onChange={(e) => setCompanyEmail(e.target.value)} placeholder="studio@email.com"
                className="mt-1 rounded-none border-[#EBE5E0] focus-visible:ring-0 focus-visible:border-[#A68A6B]" />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-[#5C5651]">Logo</Label>
            <div className="mt-2 flex items-center gap-4">
              <div className="w-20 h-20 border border-[#EBE5E0] bg-[#F9F8F6] flex items-center justify-center overflow-hidden shrink-0">
                {preview ? (
                  <img src={fileSrc(preview)} alt="Logo preview" data-testid="branding-logo-preview" className="w-full h-full object-contain" />
                ) : (
                  <Building2 size={26} strokeWidth={1.25} className="text-[#C9BBAE]" />
                )}
              </div>
              <div className="space-y-2">
                <input data-testid="branding-logo-input" type="file" accept="image/*" onChange={pick}
                  className="block w-full text-sm text-[#5C5651] file:mr-3 file:border file:border-[#A68A6B] file:bg-transparent file:text-[#A68A6B] file:px-4 file:py-2 file:uppercase file:text-xs file:tracking-wider" />
                {preview && (
                  <button type="button" data-testid="branding-remove-logo" onClick={removeLogo}
                    className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-[#9E4747] hover:underline">
                    <Trash2 size={13} strokeWidth={1.5} /> Remove logo
                  </button>
                )}
              </div>
            </div>
          </div>
          <Button data-testid="branding-save-btn" type="submit" disabled={busy}
            className="w-full rounded-none bg-[#A68A6B] hover:bg-[#8C7154] text-white uppercase tracking-wider text-xs py-5">
            {busy ? "Saving…" : "Save branding"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
